import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlane,
  FaSync,
  FaSearch,
  FaTimes,
  FaCalendarAlt,
  FaClock,
  FaMapMarkerAlt,
  FaUsers,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSuitcase,
  FaUtensils,
  FaChair,
} from "react-icons/fa";
import { Table, Input, Steps, message } from "antd";
import { useDispatch } from "react-redux";
import { fetchUserBookedTicked } from "../redux/reduxThunk/thunkBooking/Booking";
import { toast } from "react-toastify";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";
import UserDocumentViewer from "./UserDocumentViewer";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2;

/** Normalize flights to array – FTD can return Onward/Return as array or object */
function toFlightsArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter((f) => f && (f.depCode || f.depCName || f.flightNo));
  if (typeof val === "object") {
    const arr = Object.values(val).filter((f) => f && typeof f === "object" && (f.depCode || f.depCName || f.flightNo));
    return arr.length > 0 ? arr : (val.depCode || val.depCName || val.flightNo ? [val] : []);
  }
  return [];
}

/** Extract onward/return flights from booking response – handles FTD's various structures */
function getFlightsFromResponse(res, direction) {
  const key = direction === "return" ? "Return" : "Onward";
  let raw =
    res?.Flights?.[key] ||
    res?.flights?.Flights?.[key] ||
    res?.flights?.[key] ||
    res?.[key];
  if (!raw && Array.isArray(res?.results)?.[0]) {
    const r0 = res.results[0];
    raw = r0?.Flights?.[key] || r0?.flights?.[key] || r0?.[key];
  }
  return toFlightsArray(raw);
}

/** Extract SSR (add-ons) from booking: response.Ssrinfo/ssrinfo and passenger[].ssrInfo (Onward/Return with Bagg, Meal, Seat) */
function getSSRFromBooking(b) {
  const res = b?.response || {};
  const ssr = res.Ssrinfo || res.ssrinfo || null;
  const passengers = b?.passenger || [];
  const out = { onward: { bagg: null, meals: [], seats: [] }, return: { bagg: null, meals: [], seats: [] } };

  const normalizeBagg = (v) => (v && (v.baggDesc || v.baggID || v.baggAmt != null) ? v : null);
  const normalizeMeals = (v) => (Array.isArray(v) ? v : v ? [v] : []);
  const normalizeSeats = (v) => (Array.isArray(v) ? v : v ? [v] : []);

  if (ssr) {
    const o = ssr.Onward || {};
    const r = ssr.Return || {};
    out.onward.bagg = normalizeBagg(o.Bagg) || (Array.isArray(o.Bagg) ? o.Bagg[0] : null);
    out.onward.meals = normalizeMeals(o.Meal);
    out.onward.seats = normalizeSeats(o.Seat);
    out.return.bagg = normalizeBagg(r.Bagg) || (Array.isArray(r.Bagg) ? r.Bagg[0] : null);
    out.return.meals = normalizeMeals(r.Meal);
    out.return.seats = normalizeSeats(r.Seat);
  }

  for (const p of passengers) {
    const pSsr = p?.ssrInfo;
    if (!pSsr) continue;
    const o = pSsr.Onward || {};
    const r = pSsr.Return || {};
    if (!out.onward.bagg) out.onward.bagg = normalizeBagg(o.Bagg) || (Array.isArray(o.Bagg) ? o.Bagg[0] : null);
    if (out.onward.meals.length === 0) out.onward.meals = normalizeMeals(o.Meal);
    if (out.onward.seats.length === 0) out.onward.seats = normalizeSeats(o.Seat);
    if (!out.return.bagg) out.return.bagg = normalizeBagg(r.Bagg) || (Array.isArray(r.Bagg) ? r.Bagg[0] : null);
    if (out.return.meals.length === 0) out.return.meals = normalizeMeals(r.Meal);
    if (out.return.seats.length === 0) out.return.seats = normalizeSeats(r.Seat);
  }

  const hasAny = out.onward.bagg || out.onward.meals.length || out.onward.seats.length ||
    out.return.bagg || out.return.meals.length || out.return.seats.length;
  return hasAny ? out : null;
}

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [rescheduleFormData, setRescheduleFormData] = useState({
    newDate: "",
    reason: "",
  });

  const dispatch = useDispatch();
  const { currency } = useCurrency();

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await dispatch(fetchUserBookedTicked()).unwrap();
      setBookings(result?.data || []);
    } catch (e) {
      setError("Failed to load bookings: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Open cancel confirmation modal
  const handleCancelClick = (booking) => {
    setSelectedBooking(booking);
    setShowCancelModal(true);
  };

  // Confirm cancellation
  const confirmCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      setActionLoadingId(
        selectedBooking._id || selectedBooking.refID || Math.random()
      );
      setError(null);

      const onwardPax = selectedBooking?.response?.ticket?.Onward?.passenger
        || selectedBooking?.response?.ticket?.passenger
        || selectedBooking?.response?.flights?.ticket?.Onward?.passenger
        || selectedBooking?.response?.flights?.ticket?.passenger
        || [];

      const returnPax = selectedBooking?.response?.ticket?.Return?.passenger
        || selectedBooking?.response?.flights?.ticket?.Return?.passenger
        || [];

      const paxId = Array.isArray(onwardPax) ? onwardPax.map(p => p.paxID).filter(Boolean).join(",") : (onwardPax?.paxID || "");
      const paxIdr = Array.isArray(returnPax) ? returnPax.map(p => p.paxID).filter(Boolean).join(",") : (returnPax?.paxID || "");

      const payload = {
        refID: selectedBooking.refID || selectedBooking.reference,
        paxId: paxId,
        paxIdr: paxIdr, // Send empty string if undefined to prevent PHP warning
        canMode: 5, // 5 = Customer Cancel
        canRemarks: "Customer cancellation request",
      };

      const res = await fetch(`${API_BASE}/api/cancelFlight`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Cancel failed");

      toast.success("Flight cancelled successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      // Update local state
      setBookings((prev) =>
        prev.map((b) =>
          b._id === selectedBooking._id
            ? { ...b, bookingStatus: "CANCELLED" }
            : b
        )
      );

      setShowCancelModal(false);
      setSelectedBooking(null);
    } catch (e) {
      toast.error(e.message || "Cancellation failed", {
        position: "top-center",
        autoClose: 3000,
      });
      setError(e.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Open reschedule confirmation modal
  const handleRescheduleClick = (booking) => {
    setSelectedBooking(booking);
    setRescheduleFormData({
      newDate: "",
      reason: "",
    });
    setShowRescheduleModal(true);
  };

  // Confirm reschedule
  const confirmRescheduleBooking = async () => {
    if (!selectedBooking) return;

    // Validate form data
    if (!rescheduleFormData.newDate) {
      toast.error("Please select a new date for rescheduling", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    try {
      setActionLoadingId(
        selectedBooking._id || selectedBooking.refID || Math.random()
      );
      setError(null);

      // Get current flight details (onward or return leg)
      let onwardFlights = getFlightsFromResponse(selectedBooking?.response || {}, "onward");
      if (onwardFlights.length === 0) {
        onwardFlights = getFlightsFromResponse(selectedBooking?.response || {}, "return");
      }
      const firstFlight = onwardFlights[0] || {};

      // Format the new date (DD-MM-YYYY format for FTD reissue API)
      const formatDateForAPI = (dateStr) => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${day}-${month}-${year}`;
      };

      // Get paxIds from the booking response
      const onwardPax = selectedBooking?.response?.ticket?.Onward?.passenger
        || selectedBooking?.response?.ticket?.passenger
        || selectedBooking?.response?.flights?.ticket?.Onward?.passenger
        || selectedBooking?.response?.flights?.ticket?.passenger
        || [];

      const returnPax = selectedBooking?.response?.ticket?.Return?.passenger
        || selectedBooking?.response?.flights?.ticket?.Return?.passenger
        || [];

      const paxId = Array.isArray(onwardPax) ? onwardPax.map(p => p.paxID).filter(Boolean).join(",") : (onwardPax?.paxID || "");
      const paxIdr = Array.isArray(returnPax) ? returnPax.map(p => p.paxID).filter(Boolean).join(",") : (returnPax?.paxID || "");

      // Prepare reschedule payload
      const payload = {
        refID: selectedBooking.refID || selectedBooking.reference,
        paxId: paxId,
        paxIdr: paxIdr,
        travelDate: formatDateForAPI(rescheduleFormData.newDate),
        flightDetail: firstFlight.flightNumber || firstFlight.flightNo || "Yes",
        travelDater: "",
        flightDetailr: "",
        reissueRemarks: rescheduleFormData.reason || "Customer request"
      };

      const res = await fetch(`${API_BASE}/api/reschedule`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success)
        throw new Error(data.message || "Reschedule request failed");

      toast.success("Reschedule request submitted successfully! Our team will contact you shortly.", {
        position: "top-center",
        autoClose: 5000,
      });

      // Update local state
      setBookings((prev) =>
        prev.map((b) =>
          b._id === selectedBooking._id
            ? { ...b, bookingStatus: "RESCHEDULE_REQUESTED" }
            : b
        )
      );

      setShowRescheduleModal(false);
      setSelectedBooking(null);
      setRescheduleFormData({ newDate: "", reason: "" });
    } catch (e) {
      toast.error(e.message || "Reschedule request failed", {
        position: "top-center",
        autoClose: 3000,
      });
      setError(e.message);
    } finally {
      setActionLoadingId(null);
    }
  };

  // Filter bookings – by refID, PNR, route, email, mobile, passenger names
  const getPnrFromBooking = (bk) => {
    const pnrOnward = bk?.response?.Ticket?.Onward?.passenger || bk?.response?.Ticket?.passenger || bk?.response?.ticket?.Onward?.passenger || bk?.response?.ticket?.passenger || [];
    const pnrRet = bk?.response?.Ticket?.Return?.passenger || bk?.response?.Ticket?.passenger || bk?.response?.ticket?.Return?.passenger || [];
    const raw = [...(Array.isArray(pnrOnward) ? pnrOnward : []), ...(Array.isArray(pnrRet) ? pnrRet : [])];
    return raw.map((p) => p?.pnr).filter(Boolean).join(" ");
  };
  const filtered = bookings?.filter((b) => {
    if (!query) return true;
    const onward = getFlightsFromResponse(b?.response || {}, "onward");
    const ret = getFlightsFromResponse(b?.response || {}, "return");
    const routeStr = [...onward, ...ret].map((f) => `${f.depCName || ""} ${f.arrCName || ""}`).join(" ");
    const paxNames = (b?.passenger || []).map((p) => `${p.fName || p.Fname || ""} ${p.lName || p.Lname || ""}`).join(" ");
    const pnrStr = getPnrFromBooking(b);
    const hay = `${b.refID || ""} ${b.bookingID || ""} ${pnrStr} ${b.email || ""} ${b.mobile || ""} ${routeStr} ${paxNames}`.toLowerCase();
    return hay.includes(query.trim().toLowerCase());
  });

  /** Get dep/arr city codes from a booking (onward-only or return-only leg) */
  const getRouteFromBooking = (b) => {
    const onward = getFlightsFromResponse(b?.response || {}, "onward");
    const ret = getFlightsFromResponse(b?.response || {}, "return");
    const flights = onward.length > 0 ? onward : ret;
    if (!flights.length) return null;
    const first = flights[0];
    const last = flights[flights.length - 1];
    return { dep: first?.depCode, arr: last?.arrCode };
  };

  /** Group round-trip bookings: pair onward+return legs into single display items */
  const groupRoundTripBookings = (list) => {
    if (!list?.length) return [];
    const rt = (list || []).filter((b) => b.tripType === "1" || b.tripType === 1);
    const others = (list || []).filter((b) => !(b.tripType === "1" || b.tripType === 1));
    const paired = new Set();
    const result = [];

    for (let i = 0; i < rt.length; i++) {
      if (paired.has(i)) continue;
      const a = rt[i];
      const rA = getRouteFromBooking(a);
      if (!rA?.dep || !rA?.arr) {
        result.push({ type: "single", booking: a });
        continue;
      }
      const tA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      let found = -1;
      for (let j = i + 1; j < rt.length; j++) {
        if (paired.has(j)) continue;
        const b = rt[j];
        const rB = getRouteFromBooking(b);
        if (!rB?.dep || !rB?.arr) continue;
        const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        const timeDiff = Math.abs(tA - tB);
        const isComplementary = rA.arr === rB.dep && rA.dep === rB.arr;
        const isCloseTime = timeDiff < 5 * 60 * 1000; // 5 min
        if (isComplementary && isCloseTime) {
          found = j;
          break;
        }
      }
      if (found >= 0) {
        paired.add(i);
        paired.add(found);
        const b = rt[found];
        const tB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        const onward = tA <= tB ? a : b;
        const returnLeg = tA <= tB ? b : a;
        result.push({ type: "roundtrip", onward, return: returnLeg });
      } else {
        result.push({ type: "single", booking: a });
      }
    }

    return [...result, ...others.map((b) => ({ type: "single", booking: b }))];
  };

  const grouped = groupRoundTripBookings(filtered);
  const groupedForDisplay = [...(grouped || [])].sort((a, b) => {
    const getDate = (it) => {
      if (it.type === "single") return new Date(it.booking?.createdAt || 0).getTime();
      return Math.max(
        new Date(it.onward?.createdAt || 0).getTime(),
        new Date(it.return?.createdAt || 0).getTime()
      );
    };
    return getDate(b) - getDate(a);
  });

  // Format date and time – supports YYYYMMDDHHMM (12) or YYYYMMDD (8); timeStr optional e.g. "10:00"
  const formatDateTime = (dateTimeStr, timeStr) => {
    if (!dateTimeStr) return { date: "N/A", time: timeStr || "N/A" };
    const str = String(dateTimeStr).replace(/\D/g, "");
    try {
      const year = str.substring(0, 4);
      const month = str.substring(4, 6);
      const day = str.substring(6, 8);
      if (!year || !month || !day) return { date: "N/A", time: timeStr || "N/A" };
      const date = new Date(`${year}-${month}-${day}`);
      const formattedDate = date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      let formattedTime = timeStr || "N/A";
      if (str.length >= 12) {
        const hour = str.substring(8, 10);
        const minute = str.substring(10, 12);
        formattedTime = `${hour}:${minute}`;
      }
      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      return { date: "N/A", time: timeStr || "N/A" };
    }
  };

  // Get stop cities for a flight segment
  const getStopCities = (flights) => {
    if (!flights || flights.length <= 1) return [];

    const stopCities = [];
    for (let i = 0; i < flights.length - 1; i++) {
      const currentFlight = flights[i];
      const nextFlight = flights[i + 1];

      if (
        currentFlight.arrCode &&
        currentFlight.arrCName &&
        nextFlight.depCode &&
        nextFlight.depCName &&
        currentFlight.arrCode === nextFlight.depCode
      ) {
        stopCities.push({
          cityCode: currentFlight.arrCode,
          cityName: currentFlight.arrCName,
          layover: currentFlight.layover || "N/A",
        });
      }
    }
    return stopCities;
  };

  return (
    <section className="py-8 sm:py-12 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3 sm:gap-0">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
            My Bookings
          </h1>
          <button
            onClick={fetchBookings}
            className="text-sm sm:text-base text-blue-600 hover:text-blue-700 flex items-center gap-2 px-3 py-1.5 sm:px-0 sm:py-0 rounded-lg sm:rounded-none hover:bg-blue-50 sm:hover:bg-transparent transition-colors"
          >
            <FaSync className="shrink-0" /> <span>Refresh</span>
          </button>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow p-3 sm:p-4 mb-4 sm:mb-6">
          <Input
            prefix={<FaSearch className="text-gray-400" />}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by PNR, route, email, etc."
            allowClear
            size="large"
            className="w-full"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 mb-4">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : groupedForDisplay?.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            No bookings found.
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            <Table
              dataSource={groupedForDisplay}
              rowKey={(item) =>
                item.type === "roundtrip"
                  ? `rt-${item.onward._id}-${item.return?._id}`
                  : (item.booking._id || item.booking.refID || String(Math.random()))
              }
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} bookings`,
                pageSizeOptions: ["10", "20", "50"],
              }}
              scroll={{ x: 900 }}
              size="middle"
              locale={{
                emptyText: (
                  <div className="py-8 text-center text-gray-600">
                    <FaPlane className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                    <p>No bookings found</p>
                  </div>
                ),
              }}
              columns={[
                {
                  title: "Route",
                  key: "route",
                  width: 220,
                  render: (_, item) => {
                    const isRT = item.type === "roundtrip";
                    const onwardB = isRT ? item.onward : item.booking;
                    const returnB = isRT ? item.return : null;
                    const onwardFlights = getFlightsFromResponse(onwardB?.response || {}, "onward");
                    const returnFlights = isRT
                      ? (getFlightsFromResponse(returnB?.response || {}, "return").length
                        ? getFlightsFromResponse(returnB?.response || {}, "return")
                        : getFlightsFromResponse(returnB?.response || {}, "onward"))
                      : getFlightsFromResponse(onwardB?.response || {}, "return");
                    const firstOnward = onwardFlights[0];
                    const lastOnward = onwardFlights[onwardFlights.length - 1] || firstOnward;
                    const firstReturn = returnFlights[0];
                    const lastReturn = returnFlights[returnFlights.length - 1] || firstReturn;
                    return (
                      <div className="font-medium text-gray-900">
                        {firstOnward ? (
                          <>
                            {firstOnward.depCName || "N/A"} → {lastOnward?.arrCName || "N/A"}
                            {isRT && returnFlights?.length > 0 && (
                              <span className="block text-sm text-blue-600 mt-0.5">
                                ⇄ {lastReturn?.arrCName || "N/A"} → {firstOnward?.depCName || "N/A"}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-sm">Ref: {onwardB?.refID || "N/A"}</span>
                        )}
                      </div>
                    );
                  },
                },
                {
                  title: "Trip",
                  key: "trip",
                  width: 110,
                  render: (_, item) => (
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${item.type === "roundtrip" ? "bg-indigo-100 text-indigo-800" : "bg-sky-100 text-sky-800"
                        }`}
                    >
                      {item.type === "roundtrip" ? "Round Trip" : "One Way"}
                    </span>
                  ),
                },
                {
                  title: "Status",
                  key: "status",
                  width: 130,
                  render: (_, item) => {
                    const onwardB = item.type === "roundtrip" ? item.onward : item.booking;
                    const returnB = item.type === "roundtrip" ? item.return : null;
                    const cancelled = onwardB.bookingStatus === "CANCELLED" || (returnB && returnB.bookingStatus === "CANCELLED");
                    const reschedule = onwardB.bookingStatus === "RESCHEDULE_REQUESTED" || (returnB && returnB.bookingStatus === "RESCHEDULE_REQUESTED");
                    return (
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${cancelled ? "bg-red-100 text-red-700" : reschedule ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
                          }`}
                      >
                        {item.type === "roundtrip" && onwardB.bookingStatus !== returnB?.bookingStatus
                          ? "ROUND TRIP"
                          : (onwardB.bookingStatus?.toUpperCase() || "CONFIRMED")}
                      </span>
                    );
                  },
                },
                {
                  title: "Service",
                  key: "service",
                  width: 120,
                  render: (_, item) => {
                    const onwardB = item.type === "roundtrip" ? item.onward : item.booking;
                    const sType = Number(onwardB?.serType);
                    const isDomestic = sType === 1 || sType === 0;
                    return (
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${isDomestic ? "bg-orange-100 text-orange-800" : "bg-purple-100 text-purple-800"
                          }`}
                      >
                        {isDomestic ? "🇮🇳 Domestic" : "🌍 International"}
                      </span>
                    );
                  },
                },
                {
                  title: "SSR",
                  key: "ssr",
                  width: 115,
                  render: (_, item) => {
                    const onwardB = item.type === "roundtrip" ? item.onward : item.booking;
                    const returnB = item.type === "roundtrip" ? item.return : null;
                    // Check explicit includeSSR field first, then fallback to FTD response ssrinfo
                    const hasSsrInResponse = (b) => {
                      const res = b?.response;
                      return !!(res?.ssrinfo || res?.Ssrinfo || res?.SSRInfo ||
                        res?.Status?.ssrinfo || res?.flights?.ssrinfo);
                    };
                    const hasSSR = onwardB?.includeSSR === true ||
                      hasSsrInResponse(onwardB) ||
                      (returnB && hasSsrInResponse(returnB));
                    return (
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${hasSSR ? "bg-teal-100 text-teal-800" : "bg-gray-100 text-gray-600"
                          }`}
                      >
                        {hasSSR ? "✅ With SSR" : "❌ Without SSR"}
                      </span>
                    );
                  },
                },
                {
                  title: "Fare",
                  key: "fare",
                  width: 120,
                  render: (_, item) => {
                    const isRT = item.type === "roundtrip";
                    const onwardB = isRT ? item.onward : item.booking;
                    const returnB = isRT ? item.return : null;
                    const fareOnward =
                      onwardB?.response?.Fare?.total?.total ||
                      onwardB?.response?.Fare?.PublishedFare ||
                      onwardB?.response?.flights?.Fare?.total?.total ||
                      onwardB?.response?.flights?.Fare?.PublishedFare ||
                      onwardB?.amount ||
                      "0";
                    const fareReturn = returnB
                      ? (returnB?.response?.Fare?.total?.total ||
                        returnB?.response?.Fare?.PublishedFare ||
                        returnB?.response?.flights?.Fare?.total?.total ||
                        returnB?.response?.flights?.Fare?.PublishedFare ||
                        returnB?.amount ||
                        "0")
                      : "0";
                    const fare = isRT ? String(Number(fareOnward) + Number(fareReturn)) : fareOnward;
                    return (
                      <span className="font-bold text-blue-600">{formatPriceSync(Number(fare) || 0, currency)}</span>
                    );
                  },
                },
                {
                  title: "Date",
                  key: "date",
                  width: 110,
                  render: (_, item) => {
                    const onwardB = item.type === "roundtrip" ? item.onward : item.booking;
                    return (
                      <span className="text-gray-700">
                        {onwardB?.createdAt ? new Date(onwardB.createdAt).toLocaleDateString() : "N/A"}
                      </span>
                    );
                  },
                },
              ]}
              expandable={{
                expandedRowRender: (record) => {
                  const item = record;
                  const isRoundTripGroup = item.type === "roundtrip";
                  const onwardB = isRoundTripGroup ? item.onward : item.booking;
                  const returnB = isRoundTripGroup ? item.return : null;
                  const b = onwardB;

                  // FTD response – onward from onward leg, return from return leg (or same doc for one-way)
                  const onwardFlights = getFlightsFromResponse(onwardB?.response || {}, "onward");
                  const returnFlights = isRoundTripGroup
                    ? (getFlightsFromResponse(returnB?.response || {}, "return").length
                      ? getFlightsFromResponse(returnB?.response || {}, "return")
                      : getFlightsFromResponse(returnB?.response || {}, "onward"))
                    : getFlightsFromResponse(b?.response || {}, "return");

                  const fareOnward =
                    onwardB?.response?.Fare?.total?.total ||
                    onwardB?.response?.Fare?.PublishedFare ||
                    onwardB?.response?.flights?.Fare?.total?.total ||
                    onwardB?.response?.flights?.Fare?.PublishedFare ||
                    onwardB?.amount ||
                    "0";
                  const fareReturn = returnB
                    ? (returnB?.response?.Fare?.total?.total ||
                      returnB?.response?.Fare?.PublishedFare ||
                      returnB?.response?.flights?.Fare?.total?.total ||
                      returnB?.response?.flights?.Fare?.PublishedFare ||
                      returnB?.amount ||
                      "0")
                    : "0";
                  const fare = isRoundTripGroup
                    ? String(Number(fareOnward) + Number(fareReturn))
                    : fareOnward;

                  const passengers = b?.passenger || [];

                  const pnrOnward =
                    onwardB?.response?.Ticket?.Onward?.passenger ||
                    onwardB?.response?.Ticket?.passenger ||
                    onwardB?.response?.ticket?.Onward?.passenger ||
                    onwardB?.response?.ticket?.passenger ||
                    [];
                  const pnrReturn = returnB
                    ? (returnB?.response?.Ticket?.Onward?.passenger ||
                      returnB?.response?.Ticket?.passenger ||
                      returnB?.response?.ticket?.Onward?.passenger ||
                      returnB?.response?.ticket?.passenger ||
                      returnB?.response?.Ticket?.Return?.passenger ||
                      returnB?.response?.Ticket?.passenger ||
                      [])
                    : [];
                  const pnrRaw = [...(Array.isArray(pnrOnward) ? pnrOnward : []), ...(Array.isArray(pnrReturn) ? pnrReturn : [])];
                  const seenPnr = new Set();
                  const pnr = pnrRaw.filter((p) => {
                    const key = p?.pnr || JSON.stringify(p);
                    if (seenPnr.has(key)) return false;
                    seenPnr.add(key);
                    return true;
                  });

                  const bookingDate = onwardB?.createdAt
                    ? new Date(onwardB.createdAt).toLocaleDateString()
                    : "N/A";

                  const totalAdults = passengers.filter(
                    (p) => p.pType?.toLowerCase() === "a" || p.pType === "adt"
                  ).length;
                  const totalChildren = passengers.filter(
                    (p) => p.pType?.toLowerCase() === "c" || p.pType === "chd"
                  ).length;
                  const totalInfants = passengers.filter(
                    (p) => p.pType?.toLowerCase() === "i" || p.pType === "inf"
                  ).length;

                  const firstOnwardFlight = onwardFlights[0];
                  const lastOnwardFlight =
                    onwardFlights[onwardFlights.length - 1] || firstOnwardFlight;
                  const firstReturnFlight = returnFlights[0];
                  const lastReturnFlight =
                    returnFlights[returnFlights.length - 1] || firstReturnFlight;

                  // Get formatted date and time – support 8-char date and separate time (FTD)
                  const onwardDepartureDateTime = formatDateTime(
                    firstOnwardFlight?.depDate,
                    firstOnwardFlight?.depTime
                  );
                  const onwardArrivalDateTime = formatDateTime(
                    lastOnwardFlight?.arrDate,
                    lastOnwardFlight?.arrTime
                  );
                  const returnDepartureDateTime = formatDateTime(
                    firstReturnFlight?.depDate,
                    firstReturnFlight?.depTime
                  );
                  const returnArrivalDateTime = formatDateTime(
                    lastReturnFlight?.arrDate,
                    lastReturnFlight?.arrTime
                  );

                  // Get stop cities
                  const onwardStopCities = getStopCities(onwardFlights);
                  const returnStopCities = getStopCities(returnFlights);

                  // Round trip: grouped pair (onward+return) or single doc with both legs
                  const isRoundTripBooking = b.tripType === "1" || b.tripType === 1;
                  const isRoundTrip =
                    isRoundTripGroup || (isRoundTripBooking && returnFlights.length > 0);

                  return (
                    <div className="p-4 sm:p-6 space-y-4 bg-gray-50/80">
                      {/* Journey Details Grid – or summary when no flight segments */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                        {onwardFlights?.length > 0 ? (
                          <>
                            {/* Onward Journey */}
                            <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                              <h4 className="text-sm sm:text-base font-semibold text-blue-800 mb-2 sm:mb-3 flex items-center gap-2">
                                <FaPlane className="text-blue-600 shrink-0" />
                                {isRoundTripBooking ? "Round Trip Journey" : "One Way Journey"}
                              </h4>
                              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-gray-600">Route:</span>
                                  <span className="font-semibold break-words">
                                    {firstOnwardFlight?.depCode} →{" "}
                                    {lastOnwardFlight?.arrCode}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-gray-600">
                                    Departure:
                                  </span>
                                  <span className="font-semibold break-words">
                                    {onwardDepartureDateTime.date} at{" "}
                                    {onwardDepartureDateTime.time}
                                  </span>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                  <span className="text-gray-600">
                                    Arrival:
                                  </span>
                                  <span className="font-semibold break-words">
                                    {onwardArrivalDateTime.date} at{" "}
                                    {onwardArrivalDateTime.time}
                                  </span>
                                </div>
                                {onwardStopCities.length > 0 && (
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                    <span className="text-gray-600">
                                      Stops:
                                    </span>
                                    <span className="font-semibold text-left sm:text-right">
                                      {onwardStopCities.map((stop, index) => (
                                        <div key={index} className="break-words">
                                          {stop.cityName} ({stop.cityCode})
                                        </div>
                                      ))}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Return Journey - Only for Round Trip */}
                            {isRoundTrip && returnFlights?.length > 0 && (
                              <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                                <h4 className="text-sm sm:text-base font-semibold text-green-800 mb-2 sm:mb-3 flex items-center gap-2">
                                  <FaPlane className="text-green-600 transform rotate-180 shrink-0" />
                                  Return Journey
                                </h4>
                                <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                    <span className="text-gray-600">
                                      Route:
                                    </span>
                                    <span className="font-semibold break-words">
                                      {firstReturnFlight?.depCode} →{" "}
                                      {lastReturnFlight?.arrCode}
                                    </span>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                    <span className="text-gray-600">
                                      Departure:
                                    </span>
                                    <span className="font-semibold break-words">
                                      {returnDepartureDateTime.date} at{" "}
                                      {returnDepartureDateTime.time}
                                    </span>
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                    <span className="text-gray-600">
                                      Arrival:
                                    </span>
                                    <span className="font-semibold break-words">
                                      {returnArrivalDateTime.date} at{" "}
                                      {returnArrivalDateTime.time}
                                    </span>
                                  </div>
                                  {returnStopCities.length > 0 && (
                                    <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                                      <span className="text-gray-600">
                                        Stops:
                                      </span>
                                      <span className="font-semibold text-left sm:text-right">
                                        {returnStopCities.map((stop, index) => (
                                          <div key={index} className="break-words">
                                            {stop.cityName} ({stop.cityCode})
                                          </div>
                                        ))}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-4 col-span-full">
                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                              <FaMapMarkerAlt className="text-blue-600" />
                              Booking summary
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                              <div><span className="text-gray-600">Ref ID:</span> <span className="font-semibold">{isRoundTripGroup ? `${onwardB.refID || "N/A"} / ${returnB?.refID || "N/A"}` : (b.refID || "N/A")}</span></div>
                              <div><span className="text-gray-600">Booked on:</span> <span className="font-semibold">{bookingDate}</span></div>
                              <div><span className="text-gray-600">Email:</span> <span className="font-semibold">{b.email || "N/A"}</span></div>
                              <div><span className="text-gray-600">Mobile:</span> <span className="font-semibold">{b.mobile || "N/A"}</span></div>
                              <div><span className="text-gray-600">Flight ID:</span> <span className="font-semibold">{b.flightID ?? "N/A"}</span></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Booking Information */}
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div className="flex items-start sm:items-center gap-2">
                          <FaUsers className="text-blue-500 shrink-0 mt-0.5 sm:mt-0" />
                          <span className="break-words">
                            <strong>Passengers:</strong> {totalAdults} Adult
                            {totalAdults !== 1 ? "s" : ""}
                            {totalChildren > 0 &&
                              `, ${totalChildren} Child${totalChildren !== 1 ? "ren" : ""
                              }`}
                            {totalInfants > 0 &&
                              `, ${totalInfants} Infant${totalInfants !== 1 ? "s" : ""
                              }`}
                          </span>
                        </div>
                        {pnr.length > 0 && (
                          <div className="break-words">
                            <span className="text-gray-500">PNR:</span>{" "}
                            <span className="font-semibold">
                              {pnr.map((p, i) => (
                                <span key={i}>
                                  {p.pnr}
                                  {i < pnr.length - 1 && ", "}
                                </span>
                              ))}
                            </span>
                          </div>
                        )}
                        <div className="break-words">
                          <span className="text-gray-500">Total Fare:</span>{" "}
                          <span className="font-bold text-blue-600">
                            {formatPriceSync(Number(fare) || 0, currency)}
                          </span>
                        </div>
                        <div className="break-words">
                          <span className="text-gray-500">Trip Type:</span>{" "}
                          <span className="font-semibold">
                            {isRoundTripBooking ? "Round Trip" : "One Way"}
                          </span>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="mt-3 flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                        <div>
                          <span className="text-gray-600">
                            Flight ID: {b.flightID || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Detailed Flight & Booking Info */}
                      <div className="border-t pt-4 mt-4 space-y-4">
                        {/* Flight Details */}
                        <div>
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                            Detailed Flight Information
                          </h4>

                          {/* Onward Flights */}
                          <div className="mb-4 sm:mb-6">
                            <h5 className="text-sm sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                              <FaPlane className="text-blue-500 shrink-0" />
                              Journey Details
                            </h5>
                            <div className="space-y-3">
                              {onwardFlights.map((flight, fIdx) => {
                                const flightDeparture = formatDateTime(
                                  flight.depDate,
                                  flight.depTime
                                );
                                const flightArrival = formatDateTime(
                                  flight.arrDate,
                                  flight.arrTime
                                );

                                return (
                                  <div
                                    key={fIdx}
                                    className="bg-gray-50 p-3 sm:p-4 rounded-lg border-l-4 border-blue-500"
                                  >
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
                                        <span className="font-semibold text-xs sm:text-sm break-words">
                                          {flight.depCName} ({flight.depCode})
                                        </span>
                                        <span className="mx-1 sm:mx-2 shrink-0">→</span>
                                        <span className="font-semibold text-xs sm:text-sm break-words">
                                          {flight.arrCName} ({flight.arrCode})
                                        </span>
                                      </div>
                                      <span className="text-xs sm:text-sm text-gray-600 bg-white px-2 py-1 rounded shrink-0">
                                        Flight: {flight.flightNumber || flight.flightNo || "N/A"}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                      {/* Departure */}
                                      <div className="space-y-1">
                                        <div className="font-medium text-gray-900">
                                          Departure
                                        </div>
                                        <div className="pl-2">
                                          <div className="flex items-center gap-1">
                                            <FaCalendarAlt className="text-xs text-blue-500 shrink-0" />
                                            <span className="break-words">
                                              {flightDeparture.date}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <FaClock className="text-xs text-blue-500 shrink-0" />
                                            <span className="break-words">
                                              {flightDeparture.time}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Arrival */}
                                      <div className="space-y-1">
                                        <div className="font-medium text-gray-900">
                                          Arrival
                                        </div>
                                        <div className="pl-2">
                                          <div className="flex items-center gap-1">
                                            <FaCalendarAlt className="text-xs text-blue-500 shrink-0" />
                                            <span className="break-words">
                                              {flightArrival.date}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <FaClock className="text-xs text-blue-500 shrink-0" />
                                            <span className="break-words">
                                              {flightArrival.time}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="mt-2 text-xs sm:text-sm text-gray-600 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                      <div>
                                        <span className="font-medium">Duration:</span>{" "}
                                        {flight.duration ||
                                          flight.flightDuration ||
                                          flight.Duration ||
                                          "N/A"}{" "}
                                        mins
                                      </div>
                                      {flight.layover && (
                                        <div>
                                          <span className="font-medium">Layover:</span>{" "}
                                          {flight.layover}
                                        </div>
                                      )}
                                    </div>
                                    <div className="mt-1 text-xs sm:text-sm text-gray-600 break-words">
                                      <span className="font-medium">Airline:</span>{" "}
                                      {flight.airName || flight.airlineName || "N/A"} ({flight.airCode || flight.airlineCode || "N/A"})
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Return Flights - Only for Round Trip */}
                          {isRoundTrip && (
                            <div>
                              <h5 className="text-sm sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2">
                                <FaPlane className="text-green-500 transform rotate-180 shrink-0" />
                                Return Journey Details
                              </h5>
                              <div className="space-y-3">
                                {returnFlights.map((flight, fIdx) => {
                                  const flightDeparture = formatDateTime(
                                    flight.depDate,
                                    flight.depTime
                                  );
                                  const flightArrival = formatDateTime(
                                    flight.arrDate,
                                    flight.arrTime
                                  );

                                  return (
                                    <div
                                      key={fIdx}
                                      className="bg-gray-50 p-3 sm:p-4 rounded-lg border-l-4 border-green-500"
                                    >
                                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-2">
                                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 min-w-0">
                                          <span className="font-semibold text-xs sm:text-sm break-words">
                                            {flight.depCName} ({flight.depCode})
                                          </span>
                                          <span className="mx-1 sm:mx-2 shrink-0">→</span>
                                          <span className="font-semibold text-xs sm:text-sm break-words">
                                            {flight.arrCName} ({flight.arrCode})
                                          </span>
                                        </div>
                                        <span className="text-xs sm:text-sm text-gray-600 bg-white px-2 py-1 rounded shrink-0">
                                          Flight: {flight.flightNumber || flight.flightNo || "N/A"}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                                        {/* Departure */}
                                        <div className="space-y-1">
                                          <div className="font-medium text-gray-900">
                                            Departure
                                          </div>
                                          <div className="pl-2">
                                            <div className="flex items-center gap-1">
                                              <FaCalendarAlt className="text-xs text-green-500 shrink-0" />
                                              <span className="break-words">
                                                {flightDeparture.date}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <FaClock className="text-xs text-green-500 shrink-0" />
                                              <span className="break-words">
                                                {flightDeparture.time}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Arrival */}
                                        <div className="space-y-1">
                                          <div className="font-medium text-gray-900">
                                            Arrival
                                          </div>
                                          <div className="pl-2">
                                            <div className="flex items-center gap-1">
                                              <FaCalendarAlt className="text-xs text-green-500 shrink-0" />
                                              <span className="break-words">
                                                {flightArrival.date}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                              <FaClock className="text-xs text-green-500 shrink-0" />
                                              <span className="break-words">
                                                {flightArrival.time}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="mt-2 text-xs sm:text-sm text-gray-600 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                        <div>
                                          <span className="font-medium">Duration:</span>{" "}
                                          {flight.duration ||
                                            flight.flightDuration ||
                                            flight.Duration ||
                                            "N/A"}{" "}
                                          mins
                                        </div>
                                        {flight.layover && (
                                          <div>
                                            <span className="font-medium">Layover:</span>{" "}
                                            {flight.layover}
                                          </div>
                                        )}
                                      </div>
                                      <div className="mt-1 text-xs sm:text-sm text-gray-600 break-words">
                                        <span className="font-medium">Airline:</span>{" "}
                                        {flight.airName || flight.airlineName || "N/A"} ({flight.airCode || flight.airlineCode || "N/A"})
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Add-ons (SSR) – Baggage, Meals, Seats */}
                        {(() => {
                          const ssrOnwardDoc = getSSRFromBooking(onwardB);
                          const ssrReturnDoc = returnB ? getSSRFromBooking(returnB) : null;
                          const ssr = isRoundTripGroup && ssrReturnDoc
                            ? {
                              onward: ssrOnwardDoc?.onward || { bagg: null, meals: [], seats: [] },
                              return: ssrReturnDoc?.return || ssrReturnDoc?.onward || { bagg: null, meals: [], seats: [] },
                            }
                            : ssrOnwardDoc;
                          if (!ssr) return null;
                          return (
                            <div>
                              <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                                Add-ons (SSR)
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                                {/* Onward add-ons */}
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                                  <h5 className="text-sm sm:text-base font-medium text-amber-900 mb-2 sm:mb-3 flex items-center gap-2">
                                    <FaPlane className="text-amber-600 shrink-0" />
                                    Onward journey
                                  </h5>
                                  <div className="space-y-2 text-xs sm:text-sm">
                                    {ssr.onward.bagg && (
                                      <div className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaSuitcase className="text-amber-600 shrink-0" />
                                          {ssr.onward.bagg.baggDesc || "Baggage"}
                                        </span>
                                        <span className="font-medium text-amber-800 shrink-0">
                                          {formatPriceSync(Number(ssr.onward.bagg.baggAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    )}
                                    {ssr.onward.meals?.map((meal, i) => (
                                      <div key={i} className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaUtensils className="text-amber-600 shrink-0" />
                                          {meal.mealDesc || `Meal ${i + 1}`}
                                          {meal.mealRef ? ` (Seg ${meal.mealRef})` : ""}
                                        </span>
                                        <span className="font-medium text-amber-800 shrink-0">
                                          {formatPriceSync(Number(meal.mealAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    ))}
                                    {ssr.onward.seats?.map((seat, i) => (
                                      <div key={i} className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaChair className="text-amber-600 shrink-0" />
                                          {seat.seatName || seat.seatID || `Seat ${i + 1}`}
                                        </span>
                                        <span className="font-medium text-amber-800 shrink-0">
                                          {formatPriceSync(Number(seat.seatAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    ))}
                                    {!ssr.onward.bagg && ssr.onward.meals?.length === 0 && ssr.onward.seats?.length === 0 && (
                                      <span className="text-gray-500">No add-ons</span>
                                    )}
                                  </div>
                                </div>
                                {/* Return add-ons */}
                                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 sm:p-4">
                                  <h5 className="text-sm sm:text-base font-medium text-emerald-900 mb-2 sm:mb-3 flex items-center gap-2">
                                    <FaPlane className="text-emerald-600 transform rotate-180 shrink-0" />
                                    Return journey
                                  </h5>
                                  <div className="space-y-2 text-xs sm:text-sm">
                                    {ssr.return.bagg && (
                                      <div className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaSuitcase className="text-emerald-600 shrink-0" />
                                          {ssr.return.bagg.baggDesc || "Baggage"}
                                        </span>
                                        <span className="font-medium text-emerald-800 shrink-0">
                                          {formatPriceSync(Number(ssr.return.bagg.baggAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    )}
                                    {ssr.return.meals?.map((meal, i) => (
                                      <div key={i} className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaUtensils className="text-emerald-600 shrink-0" />
                                          {meal.mealDesc || `Meal ${i + 1}`}
                                          {meal.mealRef ? ` (Seg ${meal.mealRef})` : ""}
                                        </span>
                                        <span className="font-medium text-emerald-800 shrink-0">
                                          {formatPriceSync(Number(meal.mealAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    ))}
                                    {ssr.return.seats?.map((seat, i) => (
                                      <div key={i} className="flex justify-between items-start gap-2">
                                        <span className="flex items-center gap-1.5 text-gray-700">
                                          <FaChair className="text-emerald-600 shrink-0" />
                                          {seat.seatName || seat.seatID || `Seat ${i + 1}`}
                                        </span>
                                        <span className="font-medium text-emerald-800 shrink-0">
                                          {formatPriceSync(Number(seat.seatAmt) || 0, currency)}
                                        </span>
                                      </div>
                                    ))}
                                    {!ssr.return.bagg && ssr.return.meals?.length === 0 && ssr.return.seats?.length === 0 && (
                                      <span className="text-gray-500">No add-ons</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* Passenger Details */}
                        <div>
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                            Passenger Details
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {passengers.map((passenger, pIdx) => (
                              <div
                                key={pIdx}
                                className="bg-gray-50 p-3 rounded-lg"
                              >
                                <div className="text-sm sm:text-base font-medium break-words">
                                  {passenger.title || passenger.Title} {passenger.fName || passenger.Fname}{" "}
                                  {passenger.lName || passenger.Lname}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
                                  Type:{" "}
                                  {passenger.pType?.toUpperCase() || "N/A"}{" "}
                                  • Age:{" "}
                                  {(() => {
                                    if (passenger.age) return passenger.age;
                                    if (!passenger.dob) return "N/A";
                                    let dobStr = String(passenger.dob);
                                    let d;
                                    if (dobStr.includes("-")) {
                                      const parts = dobStr.split("-");
                                      if (parts[0].length === 2 && parts[2].length === 4) {
                                        d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`); // DD-MM-YYYY
                                      } else {
                                        d = new Date(dobStr);
                                      }
                                    } else if (dobStr.length === 8 && !dobStr.includes("-")) {
                                      // format YYYYMMDD
                                      const y = dobStr.substring(0, 4);
                                      const m = dobStr.substring(4, 6);
                                      const day = dobStr.substring(6, 8);
                                      d = new Date(`${y}-${m}-${day}`);
                                    } else {
                                      d = new Date(dobStr);
                                    }
                                    return isNaN(d.getTime()) ? "N/A" : new Date().getFullYear() - d.getFullYear();
                                  })()}
                                </div>
                                {b.selectedSeats && (() => {
                                  // Map passenger index to seat key, usually e.g., "pax_0_onward"
                                  const onwardSeat = b.selectedSeats[`pax_${pIdx}_onward`] || b.selectedSeats[`pax_${pIdx}`];
                                  const returnSeat = b.selectedSeats[`pax_${pIdx}_return`];
                                  if (!onwardSeat && !returnSeat) return null;
                                  return (
                                    <div className="text-xs sm:text-sm text-blue-600 font-medium mt-1">
                                      Seat: {onwardSeat?.seatName || onwardSeat?.seatID || "—"} {returnSeat ? ` (Return: ${returnSeat?.seatName || returnSeat?.seatID || "—"})` : ""}
                                    </div>
                                  );
                                })()}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Contact Information */}
                        <div>
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">
                            Contact Information
                          </h4>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-xs sm:text-sm space-y-1">
                              <div className="break-words">
                                <span className="font-medium">Email:</span>{" "}
                                {b.email || "N/A"}
                              </div>
                              <div className="break-words">
                                <span className="font-medium">Mobile:</span>{" "}
                                {b.mobile || "N/A"}
                              </div>
                              <div>
                                <span className="font-medium">
                                  Booking Date:
                                </span>{" "}
                                {bookingDate}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price Breakdown */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                            Price Breakdown
                          </h4>
                          {b.priceSummary ? (
                            <div className="space-y-1 text-sm sm:text-base text-gray-700">
                              <div className="flex justify-between">
                                <span>Base Fare (incl. Taxes)</span>
                                <span>{formatPriceSync(b.priceSummary.baseFare || 0, currency)}</span>
                              </div>
                              {Number(b.priceSummary.ssrAmount) > 0 && (
                                <div className="flex justify-between">
                                  <span>Baggage & Meals</span>
                                  <span>{formatPriceSync(b.priceSummary.ssrAmount, currency)}</span>
                                </div>
                              )}
                              {Number(b.priceSummary.seatTotalCost) > 0 && (
                                <div className="flex justify-between">
                                  <span>Seat Selection</span>
                                  <span>{formatPriceSync(b.priceSummary.seatTotalCost, currency)}</span>
                                </div>
                              )}
                              {Number(b.priceSummary.webCheckin) > 0 && (
                                <div className="flex justify-between">
                                  <span>Web Check-in</span>
                                  <span>{formatPriceSync(b.priceSummary.webCheckin, currency)}</span>
                                </div>
                              )}
                              {Number(b.priceSummary.markup) > 0 && (
                                <div className="flex justify-between text-gray-500">
                                  <span>Convenience Fee</span>
                                  <span>{formatPriceSync(b.priceSummary.markup, currency)}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-bold text-gray-900 pt-2 border-t border-gray-100 mt-2">
                                <span>Total Amount Paid</span>
                                <span>{formatPriceSync(b.amount || b.priceSummary.total || 0, currency)}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1 text-sm sm:text-base text-gray-700">
                              <div className="flex justify-between font-bold text-gray-900">
                                <span>Total Amount Paid</span>
                                <span>{formatPriceSync(b.amount || 0, currency)}</span>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">Detailed breakdown unavailable for this booking.</div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Custom Documents Section */}
                      {onwardB?.eTicketPdfPath && (
                        <div className="mt-4 p-4 bg-green-50 rounded-xl border border-green-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                          <div>
                            <h4 className="font-semibold text-green-900">Custom E-Ticket / Document</h4>
                            <p className="text-sm text-green-800">Your custom document is ready for download.</p>
                          </div>
                          <a
                            href={`${API_BASE}/${onwardB.eTicketPdfPath}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm transition-colors"
                          >
                            <FaDownload />
                            Download
                          </a>
                        </div>
                      )}

                      {onwardB?.adminDocuments && onwardB.adminDocuments.length > 0 && (
                        <div className="mt-4">
                          <UserDocumentViewer adminDocuments={onwardB.adminDocuments} />
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end mt-4 sm:mt-6 pt-4 border-t gap-3 sm:gap-0">
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 flex-wrap">
                          {isRoundTripGroup ? (
                            <>
                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center">
                                <span className="text-xs font-medium text-gray-500 shrink-0">Onward:</span>
                                <button
                                  disabled={
                                    actionLoadingId !== null ||
                                    onwardB.bookingStatus === "CANCELLED"
                                  }
                                  onClick={() => handleRescheduleClick(onwardB)}
                                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                  <FaSync className="shrink-0" />
                                  Reschedule
                                </button>
                                <button
                                  disabled={
                                    actionLoadingId !== null ||
                                    onwardB.bookingStatus === "CANCELLED"
                                  }
                                  onClick={() => handleCancelClick(onwardB)}
                                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                  <FaTimes className="shrink-0" />
                                  Cancel
                                </button>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-center">
                                <span className="text-xs font-medium text-gray-500 shrink-0">Return:</span>
                                <button
                                  disabled={
                                    actionLoadingId !== null ||
                                    returnB?.bookingStatus === "CANCELLED"
                                  }
                                  onClick={() => handleRescheduleClick(returnB)}
                                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                  <FaSync className="shrink-0" />
                                  Reschedule
                                </button>
                                <button
                                  disabled={
                                    actionLoadingId !== null ||
                                    returnB?.bookingStatus === "CANCELLED"
                                  }
                                  onClick={() => handleCancelClick(returnB)}
                                  className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                                >
                                  <FaTimes className="shrink-0" />
                                  Cancel
                                </button>
                              </div>
                            </>
                          ) : (
                            <>
                              <button
                                disabled={
                                  actionLoadingId !== null ||
                                  b.bookingStatus === "CANCELLED"
                                }
                                onClick={() => handleRescheduleClick(b)}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                              >
                                <FaSync className="shrink-0" />
                                Reschedule
                              </button>
                              <button
                                disabled={
                                  actionLoadingId !== null ||
                                  b.bookingStatus === "CANCELLED"
                                }
                                onClick={() => handleCancelClick(b)}
                                className="w-full sm:w-auto px-4 sm:px-6 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2 text-sm sm:text-base"
                              >
                                <FaTimes className="shrink-0" />
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      <AnimatePresence>
        {showCancelModal && selectedBooking && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowCancelModal(false);
              setSelectedBooking(null);
            }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="bg-red-100 p-2 sm:p-3 rounded-full shrink-0">
                      <FaExclamationTriangle className="text-red-600 text-lg sm:text-xl" />
                    </div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 break-words">
                      Confirm Cancellation
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowCancelModal(false);
                      setSelectedBooking(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Warning:</strong> Cancelling your flight booking
                      will result in cancellation charges as per airline policy.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      Booking Details
                    </h3>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">
                          Booking Reference:
                        </span>
                        <span className="font-semibold break-words">
                          {selectedBooking.refID || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">Route:</span>
                        <span className="font-semibold break-words flex flex-col items-start sm:items-end">
                          {(() => {
                            const onwardFlights = getFlightsFromResponse(selectedBooking?.response || {}, "onward");
                            const returnFlights = getFlightsFromResponse(selectedBooking?.response || {}, "return");

                            const renderRoute = (flights, label) => {
                              if (!flights || flights.length === 0) return null;
                              const first = flights[0];
                              const last = flights[flights.length - 1];
                              const stops = Math.max(0, flights.length - 1);
                              const stopText = stops === 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`;

                              return (
                                <div key={label} className="text-left sm:text-right mb-1">
                                  <div>{first?.depCName || first?.depCode || "N/A"} → {last?.arrCName || last?.arrCode || "N/A"} {label ? `(${label})` : ""}</div>
                                  <div className="text-xs text-gray-500 font-normal">{stopText}</div>
                                </div>
                              );
                            };

                            const routeDisplays = [];
                            if (onwardFlights.length > 0) routeDisplays.push(renderRoute(onwardFlights, returnFlights.length > 0 ? "Onward" : ""));
                            if (returnFlights.length > 0) routeDisplays.push(renderRoute(returnFlights, onwardFlights.length > 0 ? "Return" : ""));

                            return routeDisplays.length > 0 ? routeDisplays : "N/A";
                          })()}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">Total Fare:</span>
                        <span className="font-semibold text-blue-600">
                          {formatPriceSync(
                            Number(selectedBooking?.response?.Fare?.total?.total ||
                              selectedBooking?.response?.flights?.Fare?.total?.total || 0),
                            currency
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                    <h3 className="text-sm sm:text-base font-semibold text-red-900 mb-2">
                      Cancellation Charges
                    </h3>
                    <div className="space-y-2 text-xs sm:text-sm text-red-800">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span>Cancellation Fee:</span>
                        <span className="font-semibold">₹500 - ₹2,000</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span>Processing Fee:</span>
                        <span className="font-semibold">₹200</span>
                      </div>
                      <div className="border-t border-red-300 pt-2 mt-2 flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0 font-bold">
                        <span>Estimated Refund:</span>
                        <span className="break-words">
                          {formatPriceSync(
                            Math.max(
                              0,
                              Number(
                                selectedBooking?.response?.Fare?.total?.total ||
                                selectedBooking?.response?.flights?.Fare?.total?.total || 0
                              ) - 2000 || 0
                            ),
                            currency
                          )}
                          {" – "}
                          {formatPriceSync(
                            Math.max(
                              0,
                              Number(
                                selectedBooking?.response?.Fare?.total?.total ||
                                selectedBooking?.response?.flights?.Fare?.total?.total || 0
                              ) - 500 || 0
                            ),
                            currency
                          )}
                        </span>
                      </div>
                      <p className="text-xs mt-2 text-red-700 break-words">
                        * Final charges may vary based on airline policy and
                        cancellation timing. Refund will be processed within
                        7-14 business days.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowCancelModal(false);
                        setSelectedBooking(null);
                      }}
                      className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm sm:text-base"
                    >
                      Keep Booking
                    </button>
                    <button
                      onClick={confirmCancelBooking}
                      disabled={actionLoadingId !== null}
                      className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {actionLoadingId !== null
                        ? "Cancelling..."
                        : "Confirm Cancellation"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reschedule Confirmation Modal */}
      <AnimatePresence>
        {showRescheduleModal && selectedBooking && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowRescheduleModal(false);
              setSelectedBooking(null);
            }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto my-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <div className="bg-yellow-100 p-2 sm:p-3 rounded-full shrink-0">
                      <FaSync className="text-yellow-600 text-lg sm:text-xl" />
                    </div>
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 break-words">
                      Confirm Reschedule
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setShowRescheduleModal(false);
                      setSelectedBooking(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 shrink-0"
                  >
                    <FaTimes className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-blue-800 break-words">
                      <strong>Note:</strong> Rescheduling your flight may incur
                      additional charges based on fare difference and airline
                      policy.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      Current Booking Details
                    </h3>
                    <div className="space-y-2 text-xs sm:text-sm">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">
                          Booking Reference:
                        </span>
                        <span className="font-semibold break-words">
                          {selectedBooking.refID || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">Current Route:</span>
                        <span className="font-semibold break-words flex flex-col items-start sm:items-end">
                          {(() => {
                            const onwardFlights = getFlightsFromResponse(selectedBooking?.response || {}, "onward");
                            const returnFlights = getFlightsFromResponse(selectedBooking?.response || {}, "return");

                            const renderRoute = (flights, label) => {
                              if (!flights || flights.length === 0) return null;
                              const first = flights[0];
                              const last = flights[flights.length - 1];
                              const stops = Math.max(0, flights.length - 1);
                              const stopText = stops === 0 ? "Non-stop" : `${stops} Stop${stops > 1 ? "s" : ""}`;

                              return (
                                <div key={label} className="text-left sm:text-right mb-1">
                                  <div>{first?.depCName || first?.depCode || "N/A"} → {last?.arrCName || last?.arrCode || "N/A"} {label ? `(${label})` : ""}</div>
                                  <div className="text-xs text-gray-500 font-normal">{stopText}</div>
                                </div>
                              );
                            };

                            const routeDisplays = [];
                            if (onwardFlights.length > 0) routeDisplays.push(renderRoute(onwardFlights, returnFlights.length > 0 ? "Onward" : ""));
                            if (returnFlights.length > 0) routeDisplays.push(renderRoute(returnFlights, onwardFlights.length > 0 ? "Return" : ""));

                            return routeDisplays.length > 0 ? routeDisplays : "N/A";
                          })()}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">Current Date:</span>
                        <span className="font-semibold">
                          {(() => {
                            const onwardFlights = getFlightsFromResponse(selectedBooking?.response || {}, "onward");
                            const returnFlights = getFlightsFromResponse(selectedBooking?.response || {}, "return");
                            const flights = onwardFlights.length > 0 ? onwardFlights : returnFlights;
                            const firstFlight = flights[0];
                            if (firstFlight?.depDate) {
                              const formatted = formatDateTime(
                                firstFlight.depDate,
                                firstFlight.depTime
                              );
                              return formatted.date;
                            }
                            return "N/A";
                          })()}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0">
                        <span className="text-gray-600">Total Fare:</span>
                        <span className="font-semibold text-blue-600">
                          {formatPriceSync(
                            Number(selectedBooking?.response?.Fare?.total?.total ||
                              selectedBooking?.response?.flights?.Fare?.total?.total || 0),
                            currency
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Reschedule Form */}
                  <div className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                      Reschedule Information
                    </h3>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        New Travel Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={rescheduleFormData.newDate}
                        onChange={(e) =>
                          setRescheduleFormData({
                            ...rescheduleFormData,
                            newDate: e.target.value,
                          })
                        }
                        min={new Date().toISOString().split("T")[0]}
                        className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Please select a date after today
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        Reason for Rescheduling (Optional)
                      </label>
                      <textarea
                        value={rescheduleFormData.reason}
                        onChange={(e) =>
                          setRescheduleFormData({
                            ...rescheduleFormData,
                            reason: e.target.value,
                          })
                        }
                        placeholder="Please provide a reason for rescheduling..."
                        rows={3}
                        className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 outline-none resize-none"
                      />
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <h3 className="text-sm sm:text-base font-semibold text-yellow-900 mb-2">
                      Reschedule Process
                    </h3>
                    <div className="space-y-2 text-xs sm:text-sm text-yellow-800">
                      <p className="text-xs mt-2 text-yellow-700">
                        * Submitting this form will send a reschedule request to the airline. Our team will contact you shortly with the final fare difference and penalty charges to confirm the reschedule.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
                    <button
                      onClick={() => {
                        setShowRescheduleModal(false);
                        setSelectedBooking(null);
                        setRescheduleFormData({ newDate: "", reason: "" });
                      }}
                      className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm sm:text-base"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmRescheduleBooking}
                      disabled={
                        actionLoadingId !== null || !rescheduleFormData.newDate
                      }
                      className="w-full sm:flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                    >
                      {actionLoadingId !== null
                        ? "Submitting..."
                        : "Submit Request"}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Bookings;
